import {
  Badge,
  Box,
  Divider,
  HStack,
  Icon,
  Link,
  Text,
  TextProps,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  InternalGetIngestionDocumentFragment,
  InternalIngestionFolderFragment,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Check,
  Copy,
  ExternalLink,
  FileText,
  FolderOpen,
  Settings,
} from 'lucide-react';
import { FC, ReactNode, useMemo } from 'react';
import { CopyPasteButton } from '~/apps/consumer/components/CopyPasteButton';
import { ValidationItem } from '../utils/validation.utils';

dayjs.extend(relativeTime);

interface DebugInfoMetricsProps {
  selectedDocument: InternalGetIngestionDocumentFragment;
  folder: InternalIngestionFolderFragment | null;
  validationResultsByContract: Map<
    string,
    {
      contractDocument: GetIngestionDocumentFragment['contractDocuments'][number];
      errors: ValidationItem[];
      successes: ValidationItem[];
      userAddedIssues: IngestionDocumentValidationErrorReviewFragment[];
      pipelineTargetsDocument?: boolean;
      pipelineTargetsForm?: boolean;
      documentAndErrorRuleCount?: number;
      isRequiredDocument?: boolean;
      fieldErrorRuleCount?: number;
    }
  >;
}

const debugTextProps = {
  color: 'compliance.debug.text',
  fontSize: 'xs',
} as const;

const sectionBoxProps = {
  bg: 'compliance.debug.code.bg',
  borderRadius: 'md',
  p: 3,
} as const;

const copyButtonProps = {
  buttonContentCopied: <Icon as={Check} size={6} />,
  buttonContentCopy: <Icon as={Copy} size={6} />,
  color: 'compliance.debug.text',
  minW: 'auto',
  size: 'xs',
  variant: 'ghost',
} as const;

const DebugText: FC<TextProps> = ({
  children,
  fontFamily,
  fontStyle,
  fontWeight,
  mr,
  noOfLines,
  opacity,
  wordBreak,
}) => (
  <Text
    {...debugTextProps}
    fontFamily={fontFamily}
    fontStyle={fontStyle}
    fontWeight={fontWeight}
    mr={mr}
    noOfLines={noOfLines}
    opacity={opacity}
    wordBreak={wordBreak}
  >
    {children}
  </Text>
);

const KeyValueRow: FC<{ label: string; value: string | React.ReactNode }> = ({
  label,
  value,
}) => (
  <HStack justify="space-between">
    <DebugText opacity={0.7}>{label}:</DebugText>
    {typeof value === 'string' ? <DebugText>{value}</DebugText> : value}
  </HStack>
);

const CopyableId: FC<{ id: string; shortId?: boolean }> = ({ id, shortId }) => (
  <HStack spacing={shortId ? 0 : 1}>
    <DebugText fontFamily="mono" mr={shortId ? 1 : 0}>
      {shortId ? `...${id.slice(-6)}` : id}
    </DebugText>
    <CopyPasteButton
      {...copyButtonProps}
      buttonContentCopied={<Icon as={Check} size={shortId ? 6 : 8} />}
      buttonContentCopy={<Icon as={Copy} size={shortId ? 6 : 8} />}
      copyText={id}
      maxH={shortId ? '1em' : undefined}
      px={shortId ? 0 : 1}
    />
  </HStack>
);

const InfoSection: FC<{
  icon: React.ElementType;
  title: string;
  children: ReactNode;
}> = ({ icon, title, children }) => (
  <Box {...sectionBoxProps}>
    <VStack align="stretch" spacing={2}>
      <HStack>
        <Icon as={icon} color="compliance.debug.text" size={12} />
        <DebugText fontWeight="medium">{title}</DebugText>
      </HStack>
      {children}
    </VStack>
  </Box>
);

const ContractDocumentItem: FC<{
  contractDoc: NonNullable<
    InternalGetIngestionDocumentFragment['contractDocuments']
  >[0];
  index: number;
  isLast: boolean;
}> = ({ contractDoc, index, isLast }) => {
  const documentName =
    contractDoc.documentVersion?.document?.name ||
    contractDoc.documentCategory ||
    'Unnamed';
  const form = contractDoc.documentVersion?.document?.form;
  const usState = contractDoc.documentVersion?.document?.usState;
  const version = contractDoc.documentVersion;
  const documentId = version?.document?.id;
  const versionId = version?.id;

  return (
    <Box borderRadius="sm" mt={2}>
      <VStack align="stretch" spacing={1}>
        <HStack justify="space-between">
          <DebugText fontWeight="medium">
            #{index + 1} {documentName}
            {form && (
              <Text as="span" {...debugTextProps} ml={1} opacity={0.7}>
                ({form}, {usState})
              </Text>
            )}
          </DebugText>
          {documentId && (
            <Link
              isExternal
              _hover={{ opacity: 0.7 }}
              href={`/internal/documents/${documentId}`}
            >
              <Icon as={ExternalLink} color="compliance.debug.text" size={12} />
            </Link>
          )}
        </HStack>

        {version && (
          <VStack align="stretch" spacing={1}>
            <HStack justify="space-between">
              <DebugText opacity={0.7}>Version ID:</DebugText>
              <HStack spacing={1}>
                <CopyableId shortId id={version.id} />
                {documentId && versionId && (
                  <Link
                    isExternal
                    _hover={{ opacity: 0.7 }}
                    href={`/internal/documents/${documentId}/${versionId}`}
                  >
                    <Icon
                      as={ExternalLink}
                      color="compliance.debug.text"
                      size={10}
                    />
                  </Link>
                )}
              </HStack>
            </HStack>

            <KeyValueRow label="Version" value={version.version} />
            <KeyValueRow label="Version Status" value={version.status} />
            <KeyValueRow
              label="Search Regex"
              value={version.searchRegex || '-'}
            />
            <KeyValueRow
              label="Category"
              value={version.document?.documentCategory || '-'}
            />
            <KeyValueRow
              label="Type"
              value={version.document?.documentType || '-'}
            />
          </VStack>
        )}
      </VStack>
      {!isLast && <Divider pt={3} />}
    </Box>
  );
};

export const DebugInfoMetrics: FC<DebugInfoMetricsProps> = ({
  selectedDocument,
  folder,
  validationResultsByContract,
}) => {
  const contractDocsCount = selectedDocument?.contractDocuments?.length || 0;
  const totalRules = useMemo(() => {
    return Array.from(validationResultsByContract.values()).reduce(
      (acc, { errors, successes }) => {
        return acc + errors.length + successes.length;
      },
      0
    );
  }, [validationResultsByContract]);

  // Build quick lookup maps from provided validationResultsByContract
  const pipelineTargetsByContract = useMemo(() => {
    const m = new Map<string, boolean>();
    validationResultsByContract.forEach((v, k) => {
      m.set(k, Boolean(v.pipelineTargetsDocument));
    });

    return m;
  }, [validationResultsByContract]);

  const pipelineTargetsFormByContract = useMemo(() => {
    const m = new Map<string, boolean>();
    validationResultsByContract.forEach((v, k) => {
      m.set(k, Boolean(v.pipelineTargetsForm));
    });

    return m;
  }, [validationResultsByContract]);

  const documentAndErrorRuleCountByContract = useMemo(() => {
    const m = new Map<string, number>();
    validationResultsByContract.forEach((v, k) => {
      m.set(k, v.documentAndErrorRuleCount ?? 0);
    });

    return m;
  }, [validationResultsByContract]);

  const fieldErrorRuleCountByContract = useMemo(() => {
    const m = new Map<string, number>();
    validationResultsByContract.forEach((v, k) => {
      m.set(k, v.fieldErrorRuleCount ?? 0);
    });

    return m;
  }, [validationResultsByContract]);

  const isRequiredDocumentByContract = useMemo(() => {
    const m = new Map<string, boolean>();
    validationResultsByContract.forEach((v, k) => {
      m.set(k, v.isRequiredDocument ?? false);
    });

    return m;
  }, [validationResultsByContract]);

  return (
    <VStack align="stretch" spacing={3}>
      <Text color="compliance.debug.text" fontSize="sm">
        Document validation execution details and processing information
      </Text>

      <VStack align="stretch" spacing={3}>
        <InfoSection icon={FileText} title="Document Information">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <DebugText opacity={0.7}>Document UUID:</DebugText>
              <CopyableId shortId id={selectedDocument.id} />
            </HStack>

            <KeyValueRow
              label="Document Name"
              value={selectedDocument.documentName}
            />

            <KeyValueRow
              label="documentUpdatedAt"
              value={
                selectedDocument.documentUpdatedAt ? (
                  <Tooltip
                    label={new Date(
                      selectedDocument.documentUpdatedAt as string
                    ).toLocaleString()}
                  >
                    <Text
                      {...debugTextProps}
                      cursor="help"
                      textDecoration="underline"
                      textDecorationStyle="dotted"
                    >
                      {dayjs(
                        selectedDocument.documentUpdatedAt as string
                      ).fromNow()}
                    </Text>
                  </Tooltip>
                ) : (
                  'not available'
                )
              }
            />

            <HStack justify="space-between">
              <DebugText opacity={0.7}>Is Blank:</DebugText>
              <Badge
                bg={
                  selectedDocument.isBlank
                    ? 'compliance.badge.error.bg'
                    : 'compliance.badge.success.bg'
                }
                color="compliance.badge.text.onSolid"
                size="xs"
                variant="solid"
              >
                {selectedDocument.isBlank ? 'YES' : 'NO'}
              </Badge>
            </HStack>

            <HStack justify="space-between">
              <DebugText opacity={0.7}>DocumentML ID:</DebugText>
              {selectedDocument.documentML?.id ? (
                <CopyableId shortId id={selectedDocument.documentML.id} />
              ) : (
                <DebugText>not available</DebugText>
              )}
            </HStack>

            <KeyValueRow
              label="DocumentML Updated At"
              value={
                selectedDocument.documentML?.updatedAt ? (
                  <Tooltip
                    label={new Date(
                      selectedDocument.documentML.updatedAt as string
                    ).toLocaleString()}
                  >
                    <Text
                      {...debugTextProps}
                      cursor="help"
                      textDecoration="underline"
                      textDecorationStyle="dotted"
                    >
                      {dayjs(
                        selectedDocument.documentML.updatedAt as string
                      ).fromNow()}
                    </Text>
                  </Tooltip>
                ) : (
                  'not available'
                )
              }
            />
          </VStack>
        </InfoSection>

        <InfoSection
          icon={FolderOpen}
          title={`Contract Documents (${contractDocsCount})`}
        >
          {contractDocsCount > 0 ? (
            <VStack align="stretch" spacing={2}>
              {selectedDocument.contractDocuments.map((contractDoc, index) => (
                <ContractDocumentItem
                  key={contractDoc.id}
                  contractDoc={contractDoc}
                  index={index}
                  isLast={
                    index === selectedDocument.contractDocuments.length - 1
                  }
                />
              ))}
            </VStack>
          ) : (
            <DebugText fontStyle="italic">
              No contract documents available
            </DebugText>
          )}
        </InfoSection>

        <InfoSection icon={Settings} title="Rules Configuration">
          <VStack align="stretch" spacing={2}>
            {Array.from(validationResultsByContract.entries()).map(
              ([contractDocId, { contractDocument }]) => {
                const pipelineTargetsDocument =
                  pipelineTargetsByContract.get(contractDocId) || false;
                const pipelineTargetsForm =
                  pipelineTargetsFormByContract.get(contractDocId) || false;
                const documentAndErrorRuleCount =
                  documentAndErrorRuleCountByContract.get(contractDocId) || 0;
                const fieldErrorRuleCount =
                  fieldErrorRuleCountByContract.get(contractDocId) || 0;
                const isRequiredDocument =
                  isRequiredDocumentByContract.get(contractDocId) || false;

                const documentCategory =
                  contractDocument.documentCategory || '';
                const documentName =
                  contractDocument.documentVersion?.document?.name || 'Unnamed';
                const form =
                  contractDocument.documentVersion?.document?.form || 'Unknown';

                const documentLabel = contractDocument.documentVersion
                  ? `${documentName} (${form})`
                  : documentCategory;

                return (
                  <Box
                    key={contractDocId}
                    border={
                      pipelineTargetsDocument || pipelineTargetsForm
                        ? '2px solid'
                        : '1px solid'
                    }
                    borderColor={
                      pipelineTargetsDocument || pipelineTargetsForm
                        ? 'compliance.status.success'
                        : 'compliance.card.border'
                    }
                    borderRadius="sm"
                    p={3}
                  >
                    <VStack align="stretch" spacing={2}>
                      <VStack alignItems="flex-start" justify="space-between">
                        <DebugText fontWeight="medium">
                          {documentLabel}
                        </DebugText>
                        {(pipelineTargetsDocument || pipelineTargetsForm) && (
                          <Badge
                            bg="compliance.badge.success.bg"
                            color="compliance.badge.text.onSolid"
                            size="sm"
                          >
                            PIPELINE TARGET
                          </Badge>
                        )}
                      </VStack>

                      <HStack justify="space-between">
                        <DebugText opacity={0.7}>Document ID Target:</DebugText>
                        <Badge
                          bg={
                            pipelineTargetsDocument
                              ? 'compliance.badge.success.bg'
                              : 'compliance.badge.error.bg'
                          }
                          color="compliance.badge.text.onSolid"
                          size="sm"
                          variant="solid"
                        >
                          {pipelineTargetsDocument ? 'YES' : 'NO'}
                        </Badge>
                      </HStack>

                      <HStack justify="space-between">
                        <DebugText opacity={0.7}>Form Target:</DebugText>
                        <Badge
                          bg={
                            pipelineTargetsForm
                              ? 'compliance.badge.success.bg'
                              : 'compliance.badge.error.bg'
                          }
                          color="compliance.badge.text.onSolid"
                          size="sm"
                          variant="solid"
                        >
                          {pipelineTargetsForm ? 'YES' : 'NO'}
                        </Badge>
                      </HStack>

                      <HStack justify="space-between">
                        <DebugText opacity={0.7}>Required Document:</DebugText>
                        <Badge
                          bg={
                            !isRequiredDocument
                              ? 'compliance.badge.success.bg'
                              : 'compliance.badge.error.bg'
                          }
                          color="compliance.badge.text.onSolid"
                          size="sm"
                          variant="solid"
                        >
                          {isRequiredDocument ? 'YES' : 'NO'}
                        </Badge>
                      </HStack>

                      <HStack justify="space-between">
                        <DebugText opacity={0.7}>
                          Pipeline Error Rules:
                        </DebugText>
                        <Badge
                          bg={
                            documentAndErrorRuleCount > 0
                              ? 'compliance.badge.info.bg'
                              : 'compliance.badge.neutral.bg'
                          }
                          color="compliance.badge.text.onSolid"
                          size="sm"
                          variant="solid"
                        >
                          {documentAndErrorRuleCount}
                        </Badge>
                      </HStack>

                      <HStack justify="space-between">
                        <DebugText opacity={0.7}>Field Error Rules:</DebugText>
                        <Badge
                          bg={
                            fieldErrorRuleCount > 0
                              ? 'compliance.badge.info.bg'
                              : 'compliance.badge.neutral.bg'
                          }
                          color="compliance.badge.text.onSolid"
                          size="sm"
                          variant="solid"
                        >
                          {fieldErrorRuleCount}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Box>
                );
              }
            )}

            <HStack
              bg="compliance.debug.code.bg"
              borderRadius="sm"
              justify="space-between"
              p={2}
            >
              <DebugText fontWeight="medium">Total Rules Executed:</DebugText>
              <Badge
                bg="compliance.debug.accent.bg"
                color="compliance.debug.accent.text"
                size="lg"
              >
                {totalRules} rules × {contractDocsCount}{' '}
                {contractDocsCount === 1 ? 'doc' : 'docs'}
              </Badge>
            </HStack>
          </VStack>
        </InfoSection>
      </VStack>

      <Box {...sectionBoxProps}>
        <Text
          color="compliance.debug.text"
          fontSize="sm"
          fontWeight="medium"
          mb={2}
        >
          Execution Summary:
        </Text>

        <VStack align="stretch" spacing={2}>
          <Box borderRadius="sm">
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <DebugText opacity={0.7}>Documents Processed:</DebugText>
                <Badge
                  bg="compliance.badge.info.bg"
                  color="compliance.badge.text.onSolid"
                  size="sm"
                >
                  {contractDocsCount}
                </Badge>
              </HStack>

              <HStack justify="space-between">
                <DebugText opacity={0.7}>Pipeline Stage:</DebugText>
                <DebugText fontFamily="mono">
                  {folder?.stage || 'N/A'}
                </DebugText>
              </HStack>

              {folder?.transactionPipeline?.id && (
                <HStack justify="space-between">
                  <DebugText opacity={0.7}>Pipeline ID:</DebugText>
                  <CopyableId shortId id={folder.transactionPipeline.id} />
                </HStack>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};
